import React, { useMemo } from 'react';
import { UserPlus, UserMinus } from 'lucide-react-native';
import {
  ActionMenuDropdown,
  type ActionMenuAction,
} from '../ActionMenuDropdown/ActionMenuDropdown';
import type { FriendStatus } from '../../../src/friends/helpers/friendStatusMap.helper';
import { FRIEND_INVITE_REASONS } from '../../../src/friends/constants/friendInvite.constants';

export interface MemberRowMenuEntry {
  userId?: string;
  name: string;
}

export interface MemberRowMenuProps<T extends MemberRowMenuEntry = MemberRowMenuEntry> {
  entry: T;
  position: { top: number; right: number };
  /** When set, disables the "Add friend" and "Remove" actions for the current user's own row. */
  currentUserId?: string | null;
  canRemove: boolean;
  /** Label for the remove action, e.g. "Remove from group" or "Remove from squad". */
  removeLabel: string;
  /** Reason shown when canRemove is false. */
  removeDisabledReason?: string;
  friendStatus: FriendStatus | null;
  isFriendRequestSending: boolean;
  /** Returns true when the userId belongs to a bot/non-human (remove + add-friend both disabled). */
  isBotUser?: (userId: string) => boolean;
  theme: { gray: Record<number, string>; error: string };
  onClose: () => void;
  onAddFriend: (entry: T) => void;
  onRemoveMember: (entry: T) => void;
}

export function MemberRowMenu<T extends MemberRowMenuEntry>({
  entry,
  position,
  currentUserId,
  canRemove,
  removeLabel,
  removeDisabledReason,
  friendStatus,
  isFriendRequestSending,
  isBotUser,
  theme,
  onClose,
  onAddFriend,
  onRemoveMember,
}: MemberRowMenuProps<T>): React.ReactElement {
  const actions = useMemo<ActionMenuAction[]>(() => {
    const isSelf = currentUserId != null && entry.userId === currentUserId;
    const isBot = entry.userId != null && (isBotUser?.(entry.userId) ?? false);
    const isPending = friendStatus === 'pending_sent' || friendStatus === 'pending_received';

    const addFriendDisabled =
      isSelf || isBot || isFriendRequestSending || friendStatus === 'accepted' || isPending;
    const addFriendReason = isFriendRequestSending
      ? FRIEND_INVITE_REASONS.REQUEST_SENDING
      : isSelf
        ? "That's you"
        : isBot
          ? undefined
          : friendStatus === 'accepted'
            ? 'Already friends'
            : friendStatus === 'pending_sent'
              ? 'Request sent'
              : friendStatus === 'pending_received'
                ? 'They invited you'
                : undefined;

    const removeDisabled = !canRemove || isSelf || isBot;
    const removeReason = removeDisabled
      ? isSelf
        ? 'Use "Leave" instead'
        : isBot
          ? undefined
          : removeDisabledReason
      : undefined;

    return [
      {
        id: 'add-friend',
        label: 'Add friend',
        icon: <UserPlus size={18} color={addFriendDisabled ? theme.gray[500] : theme.gray[700]} />,
        disabled: addFriendDisabled,
        reason: addFriendReason,
        onPress: () => onAddFriend(entry),
      },
      {
        id: 'remove-member',
        label: removeLabel,
        icon: <UserMinus size={18} color={removeDisabled ? theme.gray[500] : theme.error} />,
        disabled: removeDisabled,
        reason: removeReason,
        danger: true,
        onPress: () => onRemoveMember(entry),
      },
    ];
  }, [
    entry,
    currentUserId,
    canRemove,
    removeLabel,
    removeDisabledReason,
    friendStatus,
    isFriendRequestSending,
    isBotUser,
    theme,
    onAddFriend,
    onRemoveMember,
  ]);

  return (
    <ActionMenuDropdown
      visible
      actions={actions}
      position={position}
      onClose={onClose}
    />
  );
}
